import apiClient from './apiService';

// Polymorphic registry service. Backed by /api/v1/utl/entity-registry,
// /api/v1/utl/lookup, /api/v1/utl/resolve.
//
// The registry describes every linkable entity in the app (Policy, SOP, Risk,
// Control, Contract Term, Incident, ...). Components like <EntityLinkPicker>
// use it to drive a "what should this task / agenda item link to?" UI.

export interface EntityType {
    id: number;
    type_code: string;          // e.g. 'POLICY', 'SOP', 'RISK'
    display_name: string;
    module_code: string;        // 'GOV','RSK','CTR',...
    table_name: string;
    label_column: string;
    code_column?: string | null;
    parent_type_code?: string | null;
    route_path?: string | null;
    icon?: string | null;
    sort_order: number;
}

export interface EntityRecord {
    id: number;
    label: string;
    code?: string | null;
    parent_id?: number | null;
}

export interface ResolvedEntity {
    found: boolean;
    type_code: string;
    display_name: string;
    module_code?: string;
    icon?: string | null;
    route_path?: string | null;
    label: string;
    code?: string | null;
    parent_id?: number | null;
}

let cachedTypes: EntityType[] | null = null;
let cachedCadenceTypes: EntityType[] | null = null;

export const entityRegistryService = {
    // cadenceOnly restricts to kinds flagged required_for_cadence (the cadence
    // "Linked to" picker). It is cached separately from the full linkable set.
    listTypes: async (linkableOnly = true, cadenceOnly = false): Promise<EntityType[]> => {
        if (cadenceOnly && cachedCadenceTypes) return cachedCadenceTypes;
        if (!cadenceOnly && linkableOnly && cachedTypes) return cachedTypes;
        const resp = await apiClient.get('/api/v1/utl/entity-registry', {
            params: { linkable_only: linkableOnly, cadence_only: cadenceOnly },
        });
        const types: EntityType[] = resp.data?.data ?? [];
        if (cadenceOnly) cachedCadenceTypes = types;
        else if (linkableOnly) cachedTypes = types;
        return types;
    },

    invalidateCache: () => { cachedTypes = null; cachedCadenceTypes = null; },

    lookup: async (
        typeCode: string,
        q?: string,
        parentId?: number,
        limit = 20
    ): Promise<EntityRecord[]> => {
        const resp = await apiClient.get('/api/v1/utl/lookup', {
            params: { type_code: typeCode, q, parent_id: parentId, limit },
        });
        return resp.data?.data ?? [];
    },

    resolve: async (typeId: number, recordId: number): Promise<ResolvedEntity> => {
        const resp = await apiClient.get('/api/v1/utl/resolve', {
            params: { type_id: typeId, record_id: recordId },
        });
        return resp.data;
    },
};
